import * as PIXI from "pixi.js";

import { TAssetAlias, TAssetData } from "@src/core/assets/Assets";
import { TextureAtlas } from "@esotericsoftware/spine-core";

// This script serves to manually load asset types that can not be loaded by pixi
// It solves the issue of hardcoded images paths not letting pixi find the cache-busted image files
export class AssetLoader {
  // Json path, image path
  public static async createSpritesheet(
    asset: [TAssetAlias, TAssetData],
  ): Promise<PIXI.Spritesheet> {
    const [assetAlias, assetData] = asset;
    if (!assetData.imageSrc) {
      throw new Error(
        "Can not create spritesheet. Please provide a valid image path",
      );
    }

    // Check if already cached
    if (PIXI.Assets.cache.has(assetAlias)) {
      return PIXI.Assets.cache.get(assetAlias);
    }

    // Manually create the atlas
    const atlasResponse = await fetch(assetData.src);
    const atlasData = await atlasResponse.json();
    atlasData.meta.image = assetData.imageSrc;

    // Load the PNG texture and create spritesheet
    const baseTexture = await PIXI.Assets.load(assetData.imageSrc);
    const spritesheet = new PIXI.Spritesheet(baseTexture, atlasData);
    await spritesheet.parse();

    PIXI.Assets.cache.set(assetAlias, spritesheet);

    return spritesheet;
  }

  // The returned URL used for creating spine animations with Spine
  public static async createSpineAtlas(
    asset: [TAssetAlias, TAssetData],
  ): Promise<any> {
    const [assetAlias, assetData] = asset;
    if (!assetData.imageSrc) {
      throw new Error(
        "Can not create spine atlas. Please provide a valid image path",
      );
    }

    // Check if already cached
    if (PIXI.Assets.cache.has(assetAlias)) {
      return PIXI.Assets.cache.get(assetAlias);
    }

    const atlasResponse = await fetch(assetData.src);
    const atlasText = await atlasResponse.text();
    const lines = atlasText.split("\n");

    // Handle multiple images by replacing image names in atlas text
    let modifiedAtlasText: string;
    let imagePaths: string[];

    if (Array.isArray(assetData.imageSrc)) {
      imagePaths = assetData.imageSrc;

      // Replace image names in atlas file with just the filenames
      let currentImageIndex = 0;
      const modifiedLines = lines.map((line) => {
        // Atlas format: image names appear on their own lines (usually first line of each page)
        // They don't have indentation and typically end with image extensions
        if (
          line.match(/\.(png|jpg|jpeg|webp)$/i) &&
          !line.startsWith(" ") &&
          !line.startsWith("\t")
        ) {
          if (currentImageIndex < imagePaths.length) {
            const imageName = imagePaths[currentImageIndex].split("/").pop();
            currentImageIndex++;
            return imageName || line;
          }
        }
        return line;
      });

      modifiedAtlasText = modifiedLines
        .filter((line) => !line.trim().startsWith("#"))
        .join("\n");
    } else {
      imagePaths = [assetData.imageSrc];

      // Single image (existing logic)
      const imageName = assetData.imageSrc.split("/").pop();
      lines[0] = imageName || lines[0];
      modifiedAtlasText = lines
        .filter((line) => !line.trim().startsWith("#"))
        .join("\n");
    }

    // Load all images
    const pixiTextures = await Promise.all(
      imagePaths.map((src) => PIXI.Assets.load(src)),
    );

    // Import the required classes
    const { TextureAtlas, SpineTexture } = await import(
      "@esotericsoftware/spine-pixi-v8"
    );

    // Create the TextureAtlas with modified text
    const atlas: TextureAtlas = new TextureAtlas(modifiedAtlasText);

    // Assign textures to atlas pages
    atlas.pages.forEach((page, index) => {
      if (index < pixiTextures.length) {
        const spineTexture = SpineTexture.from(pixiTextures[index].source);
        page.setTexture(spineTexture);
      } else {
        // Fallback to first texture if we run out of images
        const spineTexture = SpineTexture.from(pixiTextures[0].source);
        page.setTexture(spineTexture);
      }
    });

    PIXI.Assets.cache.set(assetAlias, atlas);
    return atlas;
  }

  public static async loadDocumentFont(
    asset: [TAssetAlias, TAssetData],
  ): Promise<void> {
    const [assetAlias, assetData] = asset;
    const font = new FontFace(assetAlias, `url(${assetData.src})`);
    await font.load();
    document.fonts.add(font);

    PIXI.Assets.cache.set(assetAlias, font);
  }

  public static async createBitmapFont(
    asset: [TAssetAlias, TAssetData],
  ): Promise<void> {
    const [assetAlias, assetData] = asset;
    if (!assetData.imageSrc) {
      throw new Error(
        "Can not create bitmap font. Please provide a valid image path",
      );
    }

    if (PIXI.Assets.cache.has(assetAlias)) {
      return PIXI.Assets.cache.get(assetAlias);
    }

    // Fetch the font data
    const fontText = await fetch(assetData.src).then((res) => res.text());
    const fontTexture = await PIXI.Assets.load(assetData.imageSrc);

    const fontData = AssetLoader._parseBMFontText(fontText);

    const bitmapFont = new PIXI.BitmapFont(
      {
        data: fontData,
        textures: [fontTexture],
      },
      assetData.src,
    );

    PIXI.Assets.cache.set(assetAlias, bitmapFont);
  }

  private static _parseBMFontText(fontText: string): PIXI.BitmapFontData {
    // Retrieve data item
    const items = fontText.match(/^[a-z]+\s+.+$/gm);
    const rawData: Record<string, any[]> = {
      info: [],
      common: [],
      page: [],
      char: [],
      chars: [],
      kerning: [],
      kernings: [],
      distanceField: [],
    };

    if (!items) {
      throw new Error("Invalid font data");
    }

    for (let i = 0; i < items.length; i++) {
      // Extract item name
      const nameMatch = items[i].match(/^[a-z]+/gm);
      if (!nameMatch) continue;
      const name = nameMatch[0];

      // Extract item attribute list as string ex.: "width=10"
      const attributeList = items[i].match(/[a-zA-Z]+=([^\s"']+|"([^"]*)")/gm);
      if (!attributeList) continue;

      // Convert attribute list into an object
      const itemData: any = {};

      for (let j = 0; j < attributeList.length; j++) {
        // Split key-value pairs
        const split = attributeList[j].split("=");
        const key = split[0];

        // Remove eventual quotes from value
        const strValue = split[1].replace(/"/gm, "");

        // Try to convert value into float
        const floatValue = parseFloat(strValue);

        // Use string value case float value is NaN
        const value = isNaN(floatValue) ? strValue : floatValue;

        itemData[key] = value;
      }

      // Push current item to the resulting data
      if (rawData[name]) {
        rawData[name].push(itemData);
      }
    }

    const font: PIXI.BitmapFontData = {
      chars: {},
      pages: [],
      lineHeight: 0,
      fontSize: 0,
      fontFamily: "",
      distanceField: undefined,
      baseLineOffset: 0,
    };

    const info = rawData.info?.[0];
    const common = rawData.common?.[0];
    const distanceField = rawData.distanceField?.[0];

    if (distanceField) {
      font.distanceField = {
        range: parseInt(distanceField.distanceRange, 10),
        type: distanceField.fieldType,
      };
    }

    if (info) {
      font.fontSize = parseInt(info.size, 10);
      font.fontFamily = info.face;
    }

    if (common) {
      font.lineHeight = parseInt(common.lineHeight, 10);
      font.baseLineOffset = font.lineHeight - parseInt(common.base, 10);
    }

    const pages = rawData.page || [];
    for (let i = 0; i < pages.length; i++) {
      font.pages.push({
        id: parseInt(pages[i].id, 10) || 0,
        file: pages[i].file,
      });
    }

    const map: Record<string, string> = {};
    const chars = rawData.char || [];

    for (let i = 0; i < chars.length; i++) {
      const charNode = chars[i];
      const id = parseInt(charNode.id, 10);

      let letter = charNode.letter ?? charNode.char ?? String.fromCharCode(id);

      if (letter === "space") letter = " ";

      map[id] = letter;

      font.chars[letter] = {
        id,
        letter, // Added missing letter property
        page: parseInt(charNode.page, 10) || 0,
        x: parseInt(charNode.x, 10),
        y: parseInt(charNode.y, 10),
        width: parseInt(charNode.width, 10),
        height: parseInt(charNode.height, 10),
        xOffset: parseInt(charNode.xoffset, 10),
        yOffset: parseInt(charNode.yoffset, 10),
        xAdvance: parseInt(charNode.xadvance, 10),
        kerning: {},
      };
    }

    // console.log('Final character map:', font.chars); // Debug log

    const kernings = rawData.kerning || [];
    for (let i = 0; i < kernings.length; i++) {
      const first = parseInt(kernings[i].first, 10);
      const second = parseInt(kernings[i].second, 10);
      const amount = parseInt(kernings[i].amount, 10);

      if (font.chars[map[second]]) {
        font.chars[map[second]].kerning[map[first]] = amount;
      }
    }

    return font;
  }
}
